/**
 * ClinicalEditTab — container for the encounter drawer's "Edit" tab (PROD-1262).
 * Fetches RunClinicalEdits for the encounter and renders the presentational rail.
 */
import { ApiError } from '@/shared/api/api-client';
import { useClinicalEdits } from './services/use-clinical-edits';
import { toClinicalEditViews } from './lib/to-clinical-edit-view';
import { ClinicalEditRail } from './clinical-edit-rail';

export interface ClinicalEditTabProps {
  encounterId?: string;
  /** Inside a titled host (the right panel's "Clinical edits" section): no second title row. */
  headless?: boolean;
}

export function ClinicalEditTab({ encounterId, headless }: ClinicalEditTabProps) {
  const { data, isLoading, isError, error, refetch } = useClinicalEdits(encounterId);
  const views = toClinicalEditViews(data ?? []);
  // PROD-1303: a 503 means the CDS engine is down ("checks unavailable") — a
  // known degraded mode, not a transport fault. Route it to the soft degraded
  // state, not the hard error banner. Any other failure stays a hard error.
  const unavailable = isError && error instanceof ApiError && error.status === 503;
  return (
    <ClinicalEditRail
      views={views}
      ready={!!encounterId}
      loading={isLoading}
      unavailable={unavailable}
      error={isError && !unavailable}
      onRetry={() => void refetch()}
      headless={headless}
    />
  );
}
